import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  TextField,
  Typography,
  Paper,
  Alert,
  InputAdornment,
  ListItemText,
  Stack,
  MenuItem,
  Divider,
} from "@mui/material";
import { isAdminUser, useUser } from "./UserContext.jsx";
import { listRoles, registerUser } from "../api/agentApi";
import { PERMISSIONS } from "../permissions";
import {
  AdminPanelSettingsOutlined,
  AlternateEmail,
  BadgeOutlined,
  LockOutlined,
  VerifiedUserOutlined,
} from "@mui/icons-material";
import { BRAND_COLORS, PAGE_SHELL, PAGE_TYPOGRAPHY, UI_RADIUS } from "../constants";

const passwordHint =
  "Password must be at least 8 characters and contain at least 1 uppercase letter, 1 numeral, and 1 special character (!@#$%^&*).";
const specialChars = "!@#$%^&*";
const borderSoft = "#E5D4C4";
const muted = "#6F6A64";

const formControlSx = (theme: any) => {
  const isDark = theme.palette.mode === "dark";
  const fieldBg = isDark ? "rgba(255,247,239,0.07)" : BRAND_COLORS.primary.softSubtle;
  const hoverBg = isDark ? "rgba(255,255,255,0.09)" : BRAND_COLORS.primary.softHover;
  const focusedBg = isDark ? "#211B16" : "#ffffff";
  const textColor = isDark ? "#FFF7EF" : "#1D1D1F";
  const mutedColor = isDark ? "#D9C9BA" : muted;
  const focusColor = isDark ? "#D1935C" : BRAND_COLORS.primary.main;

  const autofillSx = {
    WebkitBoxShadow: `0 0 0 100px ${focusedBg} inset`,
    WebkitTextFillColor: textColor,
    caretColor: textColor,
    transition: "background-color 9999s ease-out 0s",
  };

  return {
    "& .MuiFilledInput-root, & .MuiOutlinedInput-root": {
      borderRadius: UI_RADIUS.input,
      backgroundColor: fieldBg,
      color: textColor,
      "&:hover": {
        backgroundColor: hoverBg,
      },
      "&.Mui-focused": {
        backgroundColor: focusedBg,
      },
    },
    "& .MuiFilledInput-input:-webkit-autofill, & .MuiOutlinedInput-input:-webkit-autofill": autofillSx,
    "& .MuiFilledInput-input:-webkit-autofill:hover, & .MuiFilledInput-input:-webkit-autofill:focus, & .MuiOutlinedInput-input:-webkit-autofill:hover, & .MuiOutlinedInput-input:-webkit-autofill:focus": autofillSx,
    "& .MuiInputAdornment-root, & .MuiSvgIcon-root": {
      color: mutedColor,
    },
    "& .MuiFormLabel-root.Mui-focused": {
      color: focusColor,
    },
  };
};

const primaryActionButtonSx = {
  borderRadius: UI_RADIUS.button,
  bgcolor: "primary.main",
  boxShadow: `0 10px 24px ${BRAND_COLORS.primary.shadow}`,
  color: "primary.contrastText",
  textTransform: "none",
  fontWeight: 900,
  minHeight: 44,
  px: 2.8,
  "&:hover": {
    bgcolor: "primary.dark",
    boxShadow: `0 14px 30px ${BRAND_COLORS.primary.shadowHover}`,
  },
};

type RoleOption = {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  is_builtin?: boolean;
  is_system?: boolean;
};

function validatePassword(password: string) {
  return (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[0-9]/.test(password) &&
    new RegExp(`[${specialChars.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}]`).test(password)
  );
}

const AddUserPage: React.FC = () => {
  const { user, can } = useUser();
  const canManageUsers = Boolean(can?.(PERMISSIONS.USERS_MANAGE));
  const canViewSystemRoles = isAdminUser(user);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [emailValid, setEmailValid] = useState(true);
  const [emailTouched, setEmailTouched] = useState(false);
  const [password, setPassword] = useState("");
  const [retypePassword, setRetypePassword] = useState("");
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [passwordValid, setPasswordValid] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const hasRequiredRoleSelection = roles.length === 0 || roleIds.length > 0;

  const canSubmit =
    username.trim().length > 0 &&
    email.trim().length > 0 &&
    emailValid &&
    passwordValid &&
    passwordsMatch &&
    hasRequiredRoleSelection &&
    !rolesLoading &&
    password.length > 0 &&
    retypePassword.length > 0;

  const defaultRoleIds = (nextRoles: RoleOption[]) => {
    const viewerRole = nextRoles.find((item) => item.slug === "viewer");
    const firstAssignableRole = nextRoles.find((item) => !item.is_system);
    const selectedRole = viewerRole || firstAssignableRole;
    return selectedRole ? [selectedRole.id] : [];
  };

  useEffect(() => {
    if (!canManageUsers) return;
    let active = true;

    const fetchRoles = async () => {
      setRolesLoading(true);
      try {
        const response = await listRoles();
        const nextRoles = (response.roles || []).filter((item: RoleOption) => canViewSystemRoles || !item.is_system);
        if (!active) return;
        setRoles(nextRoles);
        setRoleIds((current) => (current.length > 0 ? current : defaultRoleIds(nextRoles)));
      } catch {
        if (active) setRoles([]);
      } finally {
        if (active) setRolesLoading(false);
      }
    };

    fetchRoles();
    return () => {
      active = false;
    };
  }, [canManageUsers, canViewSystemRoles]);

  if (!user || !canManageUsers) {
    return (
      <Box sx={{ pl: PAGE_SHELL.paddingLeft, pr: PAGE_SHELL.paddingRight, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto" }}>
        <Alert severity="error">You do not have permission to add users.</Alert>
      </Box>
    );
  }

  const handlePasswordChange = (val: string) => {
    setPassword(val);
    setPasswordValid(validatePassword(val));
    setPasswordsMatch(val === retypePassword);
  };

  function validateEmail(email: string) {
    // Simple regex for email validation
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  }

  const handleEmailChange = (val: string) => {
    setEmail(val);
    if (emailTouched) {
      setEmailValid(validateEmail(val));
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    setEmailValid(validateEmail(email));
  };

  const handleRetypePasswordChange = (val: string) => {
    setRetypePassword(val);
    setPasswordsMatch(password === val);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!passwordsMatch) {
      setError("Passwords must match.");
      return;
    }
    if (!validatePassword(password)) {
      setError("Password does not meet requirements.");
      return;
    }
    setLoading(true);
    try {
      await registerUser({ username, email, password, role: "user", role_ids: roleIds });
      setSuccess("User registered successfully.");
      setUsername("");
      setEmail("");
      setPassword("");
      setRetypePassword("");
      setRoleIds(defaultRoleIds(roles));
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to register user.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        pl: PAGE_SHELL.paddingLeft,
        pr: PAGE_SHELL.paddingRight,
        py: PAGE_SHELL.paddingY,
        maxWidth: PAGE_SHELL.maxWidth,
        mx: "auto",
      }}
    >
      <Stack spacing={1.9} sx={{ mb: PAGE_SHELL.headerMarginBottom }}>
        <Box>
          <Typography variant="h3" component="h1" sx={PAGE_TYPOGRAPHY.heading}>
            Add User
          </Typography>
          <Typography variant="body2" sx={{ color: muted, mt: 0.35 }}>
            Create a secure account and assign the right workspace role.
          </Typography>
        </Box>
      </Stack>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          borderRadius: UI_RADIUS.panel,
          border: `1px solid ${borderSoft}`,
          bgcolor: "background.paper",
          boxShadow: "0 22px 70px rgba(20,18,15,0.08)",
          overflow: "hidden",
        }}
      >
        <Box component="form" onSubmit={handleSubmit} sx={{ px: { xs: 2.5, md: 3 }, py: { xs: 2.5, md: 3 } }}>
          <Stack spacing={2.2}>
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeOutlined />
                    </InputAdornment>
                  ),
                }}
                sx={formControlSx}
              />
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={e => handleEmailChange(e.target.value)}
                onBlur={handleEmailBlur}
                fullWidth
                required
                error={!emailValid && email.length > 0 && emailTouched}
                helperText={!emailValid && email.length > 0 && emailTouched ? "Please enter a valid email address" : " "}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AlternateEmail />
                    </InputAdornment>
                  ),
                }}
                sx={formControlSx}
              />
            </Stack>

            <TextField
              select
              label="RBAC roles"
              value={roleIds}
              onChange={(event) => {
                const value = event.target.value;
                setRoleIds(typeof value === "string" ? value.split(",") : value);
              }}
              fullWidth
              disabled={rolesLoading || roles.length === 0}
              helperText={
                roles.length > 0
                  ? "Select the permission-based roles for this user."
                  : "No RBAC roles are available. The backend default role mapping will be used."
              }
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  const selectedIds = selected as string[];
                  return roles
                    .filter((item) => selectedIds.includes(item.id))
                    .map((item) => item.name)
                    .join(", ");
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AdminPanelSettingsOutlined />
                  </InputAdornment>
                ),
              }}
              sx={formControlSx}
            >
              {roles.map((roleOption) => (
                <MenuItem key={roleOption.id} value={roleOption.id} disabled={roleOption.is_system}>
                  <Checkbox checked={roleIds.includes(roleOption.id)} />
                  <ListItemText
                    primary={roleOption.name}
                    secondary={roleOption.is_system ? "System role, assignable only to service accounts." : roleOption.description || roleOption.slug}
                  />
                </MenuItem>
              ))}
            </TextField>

            <Divider />

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={e => handlePasswordChange(e.target.value)}
                fullWidth
                required
                helperText={passwordHint}
                error={!passwordValid && password.length > 0}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined />
                    </InputAdornment>
                  ),
                }}
                sx={formControlSx}
              />
              <TextField
                label="Confirm password"
                type="password"
                value={retypePassword}
                onChange={e => handleRetypePasswordChange(e.target.value)}
                fullWidth
                required
                error={!passwordsMatch && retypePassword.length > 0}
                helperText={!passwordsMatch && retypePassword.length > 0 ? "Passwords must match" : " "}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VerifiedUserOutlined />
                    </InputAdornment>
                  ),
                }}
                sx={formControlSx}
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="flex-end" sx={{ pt: 1 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !canSubmit}
                sx={primaryActionButtonSx}
              >
                {loading ? "Creating..." : "Create user"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default AddUserPage;
